<template>
    <div v-if="visible" class="audit-detail-overlay" @click.self="$emit('close')">
        <div class="audit-detail-modal">
            <div class="modal-header">
                <h3>감사 로그 상세 #{{ detail && detail.logIdx }}</h3>
                <button type="button" class="btn-close" @click="$emit('close')" title="닫기">
                    <i class="fas fa-times"></i>
                </button>
            </div>
            <div v-if="loading" class="modal-body loading"><i class="fas fa-spinner fa-spin"></i> 로딩 중...</div>
            <div v-else-if="error" class="modal-body error">
                {{ error }}
            </div>
            <div v-else-if="detail" class="modal-body">
                <div class="detail-section">
                    <h4 class="section-title">누가 (Who)</h4>
                    <div class="detail-rows">
                        <div class="detail-row">
                            <span class="detail-label">작업자</span>
                            <span class="detail-value"
                                >{{ detail.worker || "-" }}
                                <span v-if="detail.userIdx" class="detail-sub"
                                    >(userIdx: {{ detail.userIdx }})</span
                                ></span
                            >
                        </div>
                    </div>
                </div>
                <div class="detail-section">
                    <h4 class="section-title">언제 (When)</h4>
                    <div class="detail-rows">
                        <div class="detail-row">
                            <span class="detail-label">작업일시</span>
                            <span class="detail-value">{{ formatDate(detail.actionDate) }}</span>
                        </div>
                        <div class="detail-row">
                            <span class="detail-label">등록일시</span>
                            <span class="detail-value">{{ formatDate(detail.regDate) }}</span>
                        </div>
                    </div>
                </div>
                <div class="detail-section">
                    <h4 class="section-title">무엇을 (What)</h4>
                    <div class="detail-rows">
                        <div class="detail-row">
                            <span class="detail-label">엔티티</span>
                            <span class="detail-value"
                                >{{ entityLabel }} <span class="detail-sub">{{ detail.entity }}</span></span
                            >
                        </div>
                        <div class="detail-row">
                            <span class="detail-label">엔티티 ID</span>
                            <span class="detail-value">{{ detail.entityIdx }}</span>
                        </div>
                        <div class="detail-row">
                            <span class="detail-label">액션</span>
                            <span class="detail-value"
                                ><span class="action-badge" :class="actionClass">{{ actionLabel }}</span></span
                            >
                        </div>
                    </div>
                </div>
                <div class="detail-section" v-if="detail.remarks">
                    <h4 class="section-title">왜 (Why)</h4>
                    <div class="detail-rows">
                        <div class="detail-row detail-row-full">
                            <span class="detail-value">{{ detail.remarks }}</span>
                        </div>
                    </div>
                </div>
                <div class="detail-section">
                    <h4 class="section-title">어디서 (Where)</h4>
                    <div class="detail-rows">
                        <div class="detail-row">
                            <span class="detail-label">IP</span>
                            <span class="detail-value">{{ detail.ipAddress || "-" }}</span>
                        </div>
                        <div class="detail-row" v-if="detail.userAgent">
                            <span class="detail-label">User-Agent</span>
                            <span class="detail-value ua">{{ detail.userAgent }}</span>
                        </div>
                    </div>
                </div>
                <div class="detail-section">
                    <h4 class="section-title">어떻게 (How) — 변경 전/후</h4>
                    <div class="data-blocks">
                        <div class="data-block">
                            <h5 class="data-block-title">변경 전</h5>
                            <pre v-if="detail.beforeData">{{ formatJson(detail.beforeData) }}</pre>
                            <p v-else class="muted">-</p>
                        </div>
                        <div class="data-block">
                            <h5 class="data-block-title">변경 후</h5>
                            <pre v-if="detail.afterData">{{ formatJson(detail.afterData) }}</pre>
                            <p v-else class="muted">-</p>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn-secondary" @click="$emit('show-entity-history', detail)">
                        <i class="fas fa-history"></i> 이 엔티티 이력 보기
                    </button>
                    <button type="button" class="btn-primary" @click="$emit('close')">닫기</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CarbonCodes from "../../../codes/carbonCodes";

export default {
    name: "AuditLogDetailModal",
    props: {
        visible: { type: Boolean, default: false },
        detail: { type: Object, default: null },
        loading: { type: Boolean, default: false },
        error: { type: String, default: "" },
    },
    computed: {
        entityLabel() {
            if (!this.detail || !this.detail.entity) return "-";
            return CarbonCodes.auditEntity.getLabel(this.detail.entity);
        },
        actionLabel() {
            if (!this.detail || !this.detail.action) return "-";
            return CarbonCodes.auditAction.getLabel(this.detail.action);
        },
        actionClass() {
            if (!this.detail || !this.detail.action) return "action-default";
            const meta = CarbonCodes.auditAction.getMeta(this.detail.action) || {};
            return `action-${meta.color || "default"}`;
        },
    },
    methods: {
        formatDate(val) {
            if (!val) return "-";
            const d = typeof val === "string" ? new Date(val) : val;
            if (isNaN(d.getTime())) return String(val);
            return d.toLocaleString("ko-KR");
        },
        formatJson(obj) {
            if (obj === null || obj === undefined) return "-";
            try {
                return typeof obj === "object" ? JSON.stringify(obj, null, 2) : String(obj);
            } catch {
                return String(obj);
            }
        },
    },
};
</script>

<style scoped>
.audit-detail-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
    padding: 20px;
}
.audit-detail-modal {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
    max-width: 640px;
    width: 100%;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
}
.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
    border-radius: 12px 12px 0 0;
}
.modal-header h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
}
.btn-close {
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    color: #64748b;
    border-radius: 6px;
    cursor: pointer;
}
.btn-close:hover {
    background: #e2e8f0;
    color: #1e293b;
}
.modal-body {
    padding: 20px;
    overflow-y: auto;
    flex: 1;
}
.modal-body.loading,
.modal-body.error {
    text-align: center;
    color: #64748b;
}
.detail-section {
    margin-bottom: 18px;
}
.detail-section:last-of-type {
    margin-bottom: 0;
}
.section-title {
    margin: 0 0 8px;
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.02em;
}
.detail-rows {
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.detail-row {
    display: grid;
    grid-template-columns: 100px 1fr;
    gap: 12px;
    align-items: baseline;
    font-size: 13px;
    padding: 4px 0;
    border-bottom: 1px solid #f1f5f9;
}
.detail-row:last-child {
    border-bottom: none;
}
.detail-row-full {
    grid-template-columns: 1fr;
}
.detail-label {
    color: #64748b;
    font-weight: 500;
    flex-shrink: 0;
}
.detail-value {
    color: #334155;
    word-break: break-word;
}
.detail-sub {
    color: #94a3b8;
    font-size: 12px;
}
.data-blocks {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}
.data-block-title {
    margin: 0 0 6px;
    font-size: 12px;
    font-weight: 600;
    color: #475569;
}
.data-block pre {
    margin: 0;
    padding: 12px;
    background: #f1f5f9;
    border-radius: 6px;
    font-size: 12px;
    overflow-x: auto;
    max-height: 200px;
    overflow-y: auto;
}
.muted {
    color: #94a3b8;
}
.ua {
    font-size: 11px;
    word-break: break-all;
}
.action-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
}
.action-green {
    background: #dcfce7;
    color: #166534;
}
.action-blue {
    background: #dbeafe;
    color: #1e40af;
}
.action-red {
    background: #fee2e2;
    color: #b91c1c;
}
.action-default {
    background: #f1f5f9;
    color: #475569;
}
.action-purple {
    background: #f3e8ff;
    color: #6b21a8;
}
.action-orange {
    background: #ffedd5;
    color: #c2410c;
}
.modal-footer {
    padding: 16px 20px;
    border-top: 1px solid #e2e8f0;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}
.btn-primary,
.btn-secondary {
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    border: none;
}
.btn-primary {
    background: #52b788;
    color: #fff;
}
.btn-secondary {
    background: #e2e8f0;
    color: #475569;
}
.btn-secondary:hover {
    background: #cbd5e1;
}
</style>
